import React, { Component } from 'react';
import styled from 'styled-components';
import KarteStore from '../../store';
import { inject, observer } from 'mobx-react';
import {
  Category,
  PopulationDataType,
  CityDataType,
  Format,
  ValueType,
  City,
  SupportCategoryType
} from '../../types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkedAlt,
  faChartLine,
  faClone
} from '@fortawesome/free-solid-svg-icons';

type Props = { karte?: KarteStore; divisionNum: number };

const categories: { [k: string]: Category } = {
  町名: Category.Chome,
  地区社協: Category.Chikushakyo,
  包括支援: Category.Hokatsushien,
  地区民児協: Category.Chikuminjikyo,
  小学校: Category.ElementarySchool,
  中学校: Category.JuniorHighSchool,
  見守り: Category.Mimamori,
  区: Category.Ku
};

const supports: { [k: string]: SupportCategoryType } = {
  要支援1: SupportCategoryType.supportNeed_1,
  要支援2: SupportCategoryType.supportNeed_2,
  要介護1: SupportCategoryType.lSupportNeed_1,
  要介護2: SupportCategoryType.lSupportNeed_2,
  要介護3: SupportCategoryType.lSupportNeed_3,
  要介護4: SupportCategoryType.lSupportNeed_4,
  要介護5: SupportCategoryType.lSupportNeed_5
};

const cities: { [k: string]: City } = {
  中原区: City.Nakahara,
  多摩区: City.Tama,
  宮前区: City.Miyamae,
  川崎区: City.Kawasaki,
  幸区: City.Saiwai,
  高津区: City.Takatsu,
  麻生区: City.Aso
};

const populationDataTypes: { [k: string]: PopulationDataType } = {
  総人口: PopulationDataType.Total,
  '年少人口（15歳未満）': PopulationDataType.Less15,
  '生産年齢人口（15～64歳）': PopulationDataType.Age15_65,
  '高齢者人口（65歳以上）': PopulationDataType.Over65,
  世帯数: PopulationDataType.Household,
  '要支援、要介護(推計)': PopulationDataType.Support
};

const cityDataTypes: { [k: string]: CityDataType } = {
  '虐待数（2016）': CityDataType.Abuse2016,
  '虐待数（2017）': CityDataType.Abuse2017,
  身体障害総数: CityDataType.PhysicallyHandicapped,
  知的障害総数: CityDataType.IntellectualHandicapped,
  '町内会加入世帯（加入率）': CityDataType.JoinCommunity,
  要介護認定者数: CityDataType.NursingCare
};

type State = {
  category?: Category;
  city?: City;
  area?: string;
  support?: string;
  dataType?: string;
  valueType?: ValueType;
  year?: number;
  format?: Format;
  isSelectBarChartData: boolean;
};

const defaultState: State = {
  category: Category.Chome,
  city: City.Nakahara,
  area: undefined,
  support: SupportCategoryType.supportNeed_1,
  dataType: '総人口',
  valueType: ValueType.Amount,
  year: 2018,
  format: Format.LineChart,
  isSelectBarChartData: false
};

@inject('karte')
@observer
class AdditionalModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const karte = this.props.karte;
    if (!karte || !karte.kartes[this.props.divisionNum]) {
      this.state = {
        ...defaultState
      };
      return;
    }

    const k = karte.kartes[this.props.divisionNum];
    let dataType: string | undefined;
    if (k.populationDataType) {
      dataType = Object.keys(populationDataTypes).find(
        key => populationDataTypes[key] === k.populationDataType
      );
    } else {
      dataType = Object.keys(cityDataTypes).find(
        key => cityDataTypes[key] === k.cityDataType
      );
    }

    this.state = {
      category: k.category ?? defaultState.category,
      city: k.city ?? defaultState.city,
      area: k.area ?? defaultState.area,
      dataType: dataType ?? defaultState.dataType,
      valueType: k.valueType ?? defaultState.valueType,
      year: k.year ?? defaultState.year,
      format: k.format ?? defaultState.format,
      support: k.support ?? defaultState.support,
      isSelectBarChartData: defaultState.isSelectBarChartData
    };
  }

  componentDidMount() {
    this.fetchAreas();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (prevState.category !== this.state.category) {
      this.fetchAreas();
    }
  }

  fetchAreas = async () => {
    if (!this.state.category || !this.props.karte) return;

    await this.props.karte.fetchAreas(this.state.category);
    const newAreas = this.getAreas();

    let area = this.state.area;

    if (!newAreas?.length) {
      area = undefined;
    } else if (!area || !newAreas.includes(area)) {
      area = newAreas[0];
    } else {
      // area state does not need to be changed
      return;
    }

    this.setState({
      area
    });
  };

  getAreas = () => {
    return this.props.karte?.getAreas(
      this.state.category,
      this.state.year,
      this.state.city
    );
  };

  handleCategoryChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const c = categories[event.currentTarget.value];
    this.setState({ category: c });
    if (c !== Category.Hokatsushien) {
      this.setState({ dataType: PopulationDataType.Total });
    }
    if (c !== Category.Ku) {
      this.setState({ isSelectBarChartData: false });
    }
    if (c === Category.Ku && this.state.format === Format.Similarity) {
      this.setState({ format: Format.Map });
    }
    if (this.state.format === Format.BarChart) {
      this.setState({ format: Format.LineChart });
    }
  };

  handleCityChange = (event: React.FormEvent<HTMLSelectElement>) => {
    this.setState({ city: cities[event.currentTarget.value] });
  };

  handleAreaChange = (event: React.FormEvent<HTMLSelectElement>) => {
    this.setState({ area: event.currentTarget.value });
  };

  handleSupportChange = (event: React.FormEvent<HTMLSelectElement>) => {
    this.setState({ support: event.currentTarget.value });
  };

  handleDataTypeChange = (event: React.FormEvent<HTMLSelectElement>) => {
    if (Object.keys(cityDataTypes).includes(event.currentTarget.value)) {
      this.setState({ valueType: ValueType.Amount, format: Format.BarChart });
    } else if (this.state.isSelectBarChartData) {
      this.setState({ format: Format.LineChart });
    }
    this.setState({
      dataType: event.currentTarget.value,
      isSelectBarChartData: Object.keys(cityDataTypes).includes(
        event.currentTarget.value
      )
    });
  };

  handleValueTypeChange = (event: React.FormEvent<HTMLInputElement>) => {
    switch (event.currentTarget.value) {
      case ValueType.Amount:
        this.setState({ valueType: ValueType.Amount });
        break;
      case ValueType.Proportion:
        this.setState({ valueType: ValueType.Proportion });
        break;
    }
  };

  handleFormatChange = (format: Format) => {
    this.setState({ format: format });
  };

  handleYearChange = (event: React.FormEvent<HTMLSelectElement>) => {
    const year = parseInt(event.currentTarget.value, 10);
    this.setState({ year: year });
  };

  handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { karte, divisionNum } = this.props;
    const {
      year,
      category,
      city,
      area,
      dataType,
      valueType,
      format,
      support
    } = this.state;
    if (
      !karte ||
      !category ||
      !city ||
      !area ||
      !dataType ||
      !valueType ||
      !format ||
      !support ||
      (format === Format.Map && !year)
    )
      return;
    // ここでステートが更新される

    karte.addKarte(divisionNum, {
      category,
      city,
      area: category === Category.Ku ? city : area,
      populationDataType: populationDataTypes[dataType],
      cityDataType: cityDataTypes[dataType],
      valueType,
      year,
      format,
      support: supports[support]
    });
    karte.closeAdditionalModal();
  };

  handleClose = () => {
    const { karte } = this.props;
    if (karte) {
      karte.closeAdditionalModal();
    }
  };

  stopPropagation = (event: React.FormEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  isDisabled = () => {
    const { karte } = this.props;
    return (
      !karte ||
      karte.isFetching ||
      (this.state.category === Category.Ku &&
        (!this.state.area || this.state.format === Format.Similarity)) ||
      !this.state.category ||
      !this.state.city ||
      !this.state.dataType ||
      !this.state.valueType ||
      !this.state.format ||
      !this.state.support ||
      (this.state.format === Format.Map && !this.state.year)
    );
  };

  render() {
    const { format, valueType } = this.state;
    const disabled = this.isDisabled();
    const areas = this.getAreas();

    return (
      <Wrapper onClick={this.handleClose}>
        <ModalContents onClick={this.stopPropagation}>
          <StyledForm onSubmit={this.handleSubmit}>
            <dl>
              <FormItemTitle>カテゴリを選ぶ</FormItemTitle>
              <StyledDD>
                <StyledSelect
                  value={this.state.category}
                  onChange={this.handleCategoryChange}
                >
                  {Object.keys(categories).map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </StyledSelect>
              </StyledDD>
              <FormItemTitle>区を選ぶ</FormItemTitle>
              <StyledDD>
                <StyledSelect
                  value={this.state.city?.replace('川崎市', '')}
                  onChange={this.handleCityChange}
                >
                  {Object.keys(cities).map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </StyledSelect>
              </StyledDD>
              {this.state.category !== Category.Ku && (
                <>
                  <FormItemTitle>エリアを選ぶ</FormItemTitle>
                  <StyledDD>
                    <StyledSelect
                      value={this.state.area}
                      onChange={this.handleAreaChange}
                    >
                      {areas?.map(a => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </StyledSelect>
                  </StyledDD>
                </>
              )}
              {this.state.format !== Format.Similarity && (
                <>
                  <FormItemTitle>データを選ぶ</FormItemTitle>
                  <StyledDD>
                    <StyledSelect
                      value={this.state.dataType}
                      onChange={this.handleDataTypeChange}
                    >
                      {Object.keys(populationDataTypes)
                        .filter(o =>
                          this.state.category === Category.Hokatsushien
                            ? o
                            : o !== PopulationDataType.Support
                        )
                        .map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      {this.state.category === Category.Ku &&
                        Object.keys(cityDataTypes).map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                    </StyledSelect>
                  </StyledDD>
                </>
              )}
              {/* // 選択されたデータタイプが要介護ではない場合は、表示しない */}
              {this.state.category === Category.Hokatsushien &&
                this.state.dataType === PopulationDataType.Support && (
                  <>
                    <FormItemTitle>介護レベルを選ぶ</FormItemTitle>
                    <StyledDD>
                      <StyledSelect
                        value={this.state.support}
                        onChange={this.handleSupportChange}
                      >
                        {Object.values(SupportCategoryType).map(c => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </StyledSelect>
                    </StyledDD>
                  </>
                )}
              {this.state.format !== Format.Similarity && (
                <StyledDD>
                  <SelectValueType>
                    <StyledLabel>
                      <input
                        type="radio"
                        checked={valueType === ValueType.Amount}
                        value={ValueType.Amount}
                        onChange={this.handleValueTypeChange}
                      />
                      総計
                    </StyledLabel>
                    {!this.state.isSelectBarChartData && (
                      <StyledLabel>
                        <input
                          type="radio"
                          checked={valueType === ValueType.Proportion}
                          value={ValueType.Proportion}
                          onChange={this.handleValueTypeChange}
                        />
                        割合（1999年との比較）
                      </StyledLabel>
                    )}
                  </SelectValueType>
                </StyledDD>
              )}
              <FormItemTitle>表示方法を選ぶ</FormItemTitle>
              <SelectFormat>
                {this.state.isSelectBarChartData ? (
                  // 以下でフォーマット（折れ線グラフ、地図、類似地域を表示などを切り替える）
                  <>
                    <SelectItem
                      selected={format === Format.BarChart}
                      onClick={() => this.handleFormatChange(Format.BarChart)}
                    >
                      <FontAwesomeIcon icon={faChartLine} size="2x" />
                      <SelectItemName selected={format === Format.BarChart}>
                        グラフ
                      </SelectItemName>
                    </SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem
                      selected={format === Format.LineChart}
                      onClick={() => this.handleFormatChange(Format.LineChart)}
                    >
                      <FontAwesomeIcon icon={faChartLine} size="2x" />
                      <SelectItemName selected={format === Format.LineChart}>
                        グラフ
                      </SelectItemName>
                    </SelectItem>
                    <SelectItem
                      selected={format === Format.Map}
                      onClick={() => this.handleFormatChange(Format.Map)}
                    >
                      <FontAwesomeIcon icon={faMapMarkedAlt} size="2x" />
                      <SelectItemName selected={format === Format.Map}>
                        地図
                      </SelectItemName>
                    </SelectItem>
                    {this.state.category !== Category.Ku && (
                      <SelectItem
                        selected={format === Format.Similarity}
                        onClick={() =>
                          this.handleFormatChange(Format.Similarity)
                        }
                      >
                        <FontAwesomeIcon icon={faClone} size="2x" />
                        <SelectItemName selected={format === Format.Similarity}>
                          類似地域
                        </SelectItemName>
                      </SelectItem>
                    )}
                  </>
                )}
              </SelectFormat>
              {(format === Format.Map || format === Format.Similarity) && (
                <>
                  <FormItemTitle>年度を選ぶ</FormItemTitle>
                  <StyledDD>
                    <StyledSelect
                      value={this.state.year}
                      onChange={this.handleYearChange}
                    >
                      {/* 2030年、2040年のデータが存在するのは包括支援のデータのみであるため */}
                      {this.state.category === Category.Hokatsushien ? (
                        <>
                          <option value="2040">2040(推計)</option>
                          <option value="2030">2030(推計)</option>
                        </>
                      ) : null}
                      <option value="2018">2018</option>
                      <option value="2017">2017</option>
                      <option value="2016">2016</option>
                      <option value="2015">2015</option>
                      <option value="2014">2014</option>
                      <option value="2013">2013</option>
                      <option value="2012">2012</option>
                      <option value="2011">2011</option>
                      <option value="2010">2010</option>
                      <option value="2009">2009</option>
                      <option value="2008">2008</option>
                      <option value="2007">2007</option>
                      <option value="2006">2006</option>
                      <option value="2005">2005</option>
                      <option value="2004">2004</option>
                      <option value="2003">2003</option>
                      <option value="2002">2002</option>
                      <option value="2001">2001</option>
                      <option value="2000">2000</option>
                      <option value="1999">1999</option>
                    </StyledSelect>
                  </StyledDD>
                </>
              )}
              <SubmitWrapper>
                <Submit type="submit" value="表示" disabled={disabled} />
              </SubmitWrapper>
            </dl>
          </StyledForm>
          <CloseButton onClick={this.handleClose}>×</CloseButton>
        </ModalContents>
      </Wrapper>
    );
  }
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
`;

const ModalContents = styled.div`
  width: 300px;
  height: 520px;
  position: absolute;
  padding: 10px 20px;
  border: 1px solid #999;
  background: white;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
`;

const CloseButton = styled.div`
  position: absolute;
  display: inline-block;
  cursor: pointer;
  top: 0;
  right: 0;
  padding: 10px;
`;

const StyledForm = styled.form`
  margin-top: 25px;
`;

const FormItemTitle = styled.dt`
  font-size: 11px;
  color: #333;
`;

const StyledDD = styled.dd`
  margin: 5px 0 10px;
`;

const SelectFormat = styled(StyledDD)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectItem = styled.div<{ selected: boolean }>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  border: solid 1px ${props => (props.selected ? `#4a90e2` : `#999`)};
  color: ${props => (props.selected ? `#4a90e2` : `#999`)};
  border-radius: 3px;
  margin: 5px;
  width: 100%;
  padding: 10px;
  cursor: pointer;
`;

const SelectItemName = styled.p<{ selected: boolean }>`
  font-size: 12px;
  color: ${props => (props.selected ? `#4a90e2` : `#999`)};
  font-weight: ${props => (props.selected ? 'bold' : `normal`)};
  margin-top: 5px;
`;

const SubmitWrapper = styled.div`
  text-align: center;
`;

const Submit = styled.input`
  width: 100%;
  outline: none;
  border: none;
  cursor: pointer;
  font-weight: bold;
  padding: 10px 0;
  background: #4a90e2;
  border-radius: 3px;
  color: #fff;

  &:disabled {
    opacity: 0.5;
  }
`;

const StyledSelect = styled.select`
  padding: 5px 0;
  border: solid 1px #ccc;
  border-radius: 3px;
  width: 100%;
  font-size: 12px;
  box-shadow: none;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  outline: none;
`;

const SelectValueType = styled.div`
  margin: 5px 0;
`;

const StyledLabel = styled.label`
  font-size: 12px;
  margin-right: 8px;
`;
export default AdditionalModal;
